import { useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "@store/index";
import {
  chartConfigSelector,
  chartConfigDispatcher,
} from "@store/slices/chart-config-slice";
import { useLazyGetDatasetQuestionsListQuery } from "@store/api/dataset-api";
import type { DatasetMetric } from "@models/Metric";
import type { QuestionDetail } from "@models/Dataset";
import {
  getMetricResultCandidates,
  metricBaseToVisualizationBase,
} from "@utils/dataset-metric-representation-utils";
import {
  barChartMetricNeedsQuestionDetailFetch,
  buildBarChartFocusParamsFromMetric,
} from "@components/charts/bar-chart-config/metric-source/bar-chart-metric-utils";
import type { useBarChartData } from "@components/charts/bar-chart-config/hooks/useBarChartData";

type FetchChartData = ReturnType<typeof useBarChartData>["fetchChartData"];


export function useMetricSourceRevalidation(
  datasetId: string | null,
  metrics: DatasetMetric[] | undefined,
  datasetLoaded: boolean,
  fetchChartData: FetchChartData,
) {
  const dispatch = useAppDispatch();
  const { currentConfig } = useAppSelector(chartConfigSelector);
  const {
    updateFocusParams: updateBarFocusParams,
    updateFocusRollup: updateBarFocusRollup,
    updateVisualizationBase,
    updateDatasetMetricSource,
    updateChartTitle,
    updateCategoryRollups: updateBarCategoryRollups,
  } = chartConfigDispatcher(dispatch);

  const isMetricSourced = currentConfig.chartSourceType === "metric";
  const datasetMetricId = currentConfig.datasetMetricId ?? null;

  const liveMetric = useMemo(
    () => (datasetMetricId ? (metrics?.find((m) => m.id === datasetMetricId) ?? null) : null),
    [metrics, datasetMetricId],
  );
  const isRemoved = isMetricSourced && !!datasetMetricId && datasetLoaded && !liveMetric;
  const isUnavailableMean = !!liveMetric?.source && liveMetric.source.resultType === "mean";

  const [questionDetail, setQuestionDetail] = useState<QuestionDetail | null>(null);
  const [fetchError, setFetchError] = useState(false);
  const [fetchQuestionDetails, { isFetching: isFetchingDetails }] =
    useLazyGetDatasetQuestionsListQuery();

  const needsFetch =
    !!liveMetric && !isUnavailableMean && barChartMetricNeedsQuestionDetailFetch(liveMetric);

  const runFetch = () => {
    if (!liveMetric?.source || !datasetId) return;
    setFetchError(false);
    fetchQuestionDetails({
      id: datasetId,
      page: 1,
      page_size: 10,
      search_query: liveMetric.source.question.questionLabel,
      dataset_element_id: liveMetric.source.question.questionId,
      expanded: true,
    })
      .unwrap()
      .then((result) => {
        const detail = result.questions.find(
          (q) => q.datasetElementId === liveMetric.source!.question.questionId,
        );
        setQuestionDetail(detail ?? null);
      })
      .catch(() => setFetchError(true));
  };

  useEffect(() => {
    if (!liveMetric?.source || !datasetId || !needsFetch) {
      setQuestionDetail(null);
      setFetchError(false);
      return;
    }
    runFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveMetric?.id, datasetId, needsFetch]);

  const freshFocusParams = useMemo(() => {
    if (!liveMetric || isUnavailableMean) return null;
    if (needsFetch && !questionDetail) return null;
    return buildBarChartFocusParamsFromMetric(liveMetric, questionDetail);
  }, [liveMetric, isUnavailableMean, needsFetch, questionDetail]);

  const definitionChanged = useMemo(() => {
    if (!liveMetric || isRemoved || isUnavailableMean || !freshFocusParams) return false;
    const oldSignature = JSON.stringify({
      base: currentConfig.datasetMetricBase,
      population: currentConfig.datasetMetricPopulation,
      focusMode: currentConfig.focusMode ?? null,
      focusEntity: currentConfig.focusEntity || null,
      focusRollup: currentConfig.focusRollup ?? null,
      // single_one_dimensional rollup only — "default" scope always, since
      // this path never sets focusMode (see buildBarChartFocusParamsFromMetric).
      categoryRollups: currentConfig.categoryRollupsByScope?.default ?? null,
    });
    const newSignature = JSON.stringify({
      base: liveMetric.base,
      population: liveMetric.population,
      focusMode: freshFocusParams.focusMode ?? null,
      focusEntity: freshFocusParams.focusEntity || null,
      focusRollup: freshFocusParams.focusRollup ?? null,
      categoryRollups: freshFocusParams.categoryRollups ?? null,
    });
    return oldSignature !== newSignature;
  }, [
    liveMetric,
    isRemoved,
    isUnavailableMean,
    freshFocusParams,
    currentConfig.datasetMetricBase,
    currentConfig.datasetMetricPopulation,
    currentConfig.focusMode,
    currentConfig.focusEntity,
    currentConfig.focusRollup,
    currentConfig.categoryRollupsByScope,
  ]);

  const representedResults = useMemo(
    () => (liveMetric ? getMetricResultCandidates(liveMetric, questionDetail) : []),
    [liveMetric, questionDetail],
  );

  // Redux-only half of a refresh — no fetch. Shared by the manual Refresh
  // button (handleRefresh, below) and the reopen-time auto-apply effect,
  // which must NOT fetch itself (see the hook's doc comment): it only needs
  // Redux to reflect the live definition before useBarChartHandlers.ts's own
  // initial-fetch-on-open effect fires, once, using it.
  const applyLiveDefinition = () => {
    if (!liveMetric || !freshFocusParams) return null;
    const wasDefaultTitle = currentConfig.title === currentConfig.datasetMetricName;

    updateBarFocusRollup(
      freshFocusParams.focusRollup
        ? { rollup: freshFocusParams.focusRollup, name: freshFocusParams.focusRollupName }
        : null,
    );
    updateBarFocusParams(
      freshFocusParams.focusMode
        ? {
            focusMode: freshFocusParams.focusMode,
            focusEntity: freshFocusParams.focusEntity ?? "",
            focusEntityDisplayLabel: freshFocusParams.focusEntityDisplayLabel,
          }
        : null,
    );
    // single_one_dimensional rollup only — always dispatched (not just when
    // present) so a live edit that removes/changes the rollup correctly
    // clears a stale one from a prior resolution, not just adds a new one.
    updateBarCategoryRollups(freshFocusParams.categoryRollups ?? null);
    const visualizationBase = metricBaseToVisualizationBase(liveMetric.base);
    updateVisualizationBase(visualizationBase);
    updateDatasetMetricSource({
      datasetMetricId: liveMetric.id,
      datasetMetricName: liveMetric.name,
      base: liveMetric.base,
      population: liveMetric.population,
      representedResults,
    });
    if (wasDefaultTitle) updateChartTitle(liveMetric.name);

    return visualizationBase;
  };

  // Manual "Refresh" (the definition-changed banner's button) — the drawer
  // is already open and showing data at this point, so this applies AND
  // immediately re-fetches to visibly update the preview.
  const handleRefresh = () => {
    if (!liveMetric) return;
    const visualizationBase = applyLiveDefinition();
    if (!visualizationBase || !freshFocusParams) return;

    void fetchChartData(
      currentConfig.weight,
      true,
      undefined,
      undefined,
      undefined,
      false,
      freshFocusParams.focusRollup ?? null,
      freshFocusParams.focusMode
        ? { focusMode: freshFocusParams.focusMode, focusEntity: freshFocusParams.focusEntity ?? "" }
        : undefined,
      // Explicit override, not read off currentConfig — same same-tick
      // dispatch race noted for useTotalBase below.
      freshFocusParams.categoryRollups ?? null,
      visualizationBase,
      // Explicit override, not read off currentConfig — applyLiveDefinition's
      // dispatch just above hasn't reached this closure's currentConfig yet
      // (same synchronous tick), so reading currentConfig.datasetMetricPopulation
      // here would still see the pre-refresh value. See useBarChartData.ts.
      liveMetric.population.mode === "total_survey_population",
    );
  };

  const hasAutoAppliedOnReopenRef = useRef(false);
  useEffect(() => {
    if (hasAutoAppliedOnReopenRef.current || !liveMetric || !freshFocusParams) return;
    hasAutoAppliedOnReopenRef.current = true;
    applyLiveDefinition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveMetric, freshFocusParams]);

  const isResolving = needsFetch && !questionDetail && !fetchError;
  const isSettled =
    !isMetricSourced || isRemoved || isUnavailableMean || (!!liveMetric && !isResolving && !definitionChanged);

  return {
    isMetricSourced,
    liveMetric,
    isRemoved,
    isUnavailableMean,
    definitionChanged,
    isResolving,
    isSettled,
    fetchError,
    isFetchingDetails,
    handleRefresh,
    retryFetch: runFetch,
    representedResults,
    /** Only needed to describe a rollup's member category labels in the
     *  summary UI (see describeMetricSelection) — null until/unless this
     *  metric's kind needs it (barChartMetricNeedsQuestionDetailFetch). */
    questionDetail,
  };
}
